import React, { useEffect, useState } from "react";
import { api, API, useAuth } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, PencilSimple, UploadSimple, FileText, WarningCircle, CheckCircle, X, Envelope, LinkSimple, Copy, ClockCounterClockwise, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

const VENDORS = ["Cisco", "Fortinet", "Palo Alto", "F5", "Checkpoint"];
const PERSON_TYPES = [
  { key: "engineer", label: "Engineer" },
  { key: "field_tech", label: "Field Technician" },
];
const STATUSES = ["available", "on-project", "on-site", "leave"];
const SKILL_LIB = ["Cable Management", "Rack & Stack", "Remote Hands", "Console Access", "Fibre Splicing", "Circuit Testing"];

// --------------------------------------------------------------------
// Timeline helpers — show the lifecycle of an invite with timestamps
// --------------------------------------------------------------------
const SYD_TZ = "Australia/Sydney";
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric", timeZone: SYD_TZ });
  } catch { return iso.slice(0, 10); }
};
const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: SYD_TZ, timeZoneName: "short",
    });
  } catch { return iso.replace("T", " ").slice(0, 16); }
};

function buildTimelineEvents(i) {
  // Returns a list of {label, at, kind: 'done'|'pending'} in chronological order
  const events = [];
  events.push({ label: "Invited", at: i.created_at, kind: i.created_at ? "done" : "pending" });
  if (i.prescreening_reminder_last_sent_at) {
    events.push({ label: `Reminder sent${i.prescreening_reminders_count ? ` (${i.prescreening_reminders_count}×)` : ""}`, at: i.prescreening_reminder_last_sent_at, kind: "done", kind_subtle: true });
  }
  events.push({ label: "Submitted", at: i.submitted_at, kind: i.submitted_at ? "done" : "pending" });
  if (i.status === "approved" || i.approved_at) {
    events.push({ label: "Approved", at: i.approved_at, kind: i.approved_at ? "done" : "pending" });
  } else if (i.status === "rejected") {
    events.push({ label: "Rejected", at: i.rejected_at, kind: "done", kind_bad: true });
  } else if (i.status === "held") {
    events.push({ label: "Held", at: i.held_at, kind: "done", kind_warn: true });
  }
  if (i.status === "approved") {
    if (i.docs_reminder_last_sent) events.push({ label: `Docs reminder${i.docs_reminders_sent ? ` (${i.docs_reminders_sent}×)` : ""}`, at: i.docs_reminder_last_sent, kind: "done", kind_subtle: true });
    events.push({ label: "Docs received", at: i.docs_received_at, kind: i.docs_received ? "done" : "pending" });
    events.push({ label: "Agreement sent", at: i.agreement_sent_manually_at, kind: i.agreement_sent_manually ? "done" : "pending" });
    events.push({ label: "Contract signed", at: i.contract_signed_manually_at, kind: i.contract_signed_manually ? "done" : "pending" });
  }
  return events;
}

function CompactTimeline({ invite }) {
  const events = buildTimelineEvents(invite);
  return (
    <div className="flex items-center gap-1 flex-wrap mt-1" data-testid={`timeline-strip-${invite.id}`}>
      {events.map((e, idx) => {
        const color = e.kind_bad ? "text-rose-700 border-rose-300 bg-rose-50"
                    : e.kind_warn ? "text-amber-800 border-amber-300 bg-amber-50"
                    : e.kind_subtle ? "text-slate-500 border-slate-200 bg-slate-50"
                    : e.kind === "done" ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                    : "text-slate-400 border-slate-200 bg-white";
        return (
          <React.Fragment key={idx}>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-wider ${color}`} title={e.at ? fmtDateTime(e.at) : "pending"}>
              {e.kind === "done" && !e.kind_bad && !e.kind_warn && !e.kind_subtle && <span>✓</span>}
              {e.kind_subtle && <span>↻</span>}
              <span>{e.label}</span>
              {e.at && <span className="text-[9px] opacity-70">· {fmtDate(e.at)}</span>}
            </span>
            {idx < events.length - 1 && <span className="text-slate-300 text-[9px]">·</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FullTimeline({ invite }) {
  const events = buildTimelineEvents(invite);
  return (
    <ul className="space-y-2" data-testid={`timeline-full-${invite.id}`}>
      {events.map((e, idx) => {
        const color = e.kind_bad ? "text-rose-700"
                    : e.kind_warn ? "text-amber-700"
                    : e.kind_subtle ? "text-slate-500"
                    : e.kind === "done" ? "text-emerald-700"
                    : "text-slate-400";
        const dot = e.kind_bad ? "bg-rose-500"
                  : e.kind_warn ? "bg-amber-500"
                  : e.kind_subtle ? "bg-slate-400"
                  : e.kind === "done" ? "bg-emerald-500"
                  : "bg-slate-300";
        return (
          <li key={idx} className="flex items-start gap-3">
            <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${dot}`} />
            <div className="flex-1">
              <div className={`text-xs font-mono uppercase tracking-wider ${color}`}>{e.label}</div>
              <div className="text-[11px] text-slate-500 font-mono">{e.at ? fmtDateTime(e.at) : "pending"}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function Personnel() {
  const { isAdmin } = useAuth();
  const [people, setPeople] = useState([]);
  const [certCatalog, setCertCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(init());
  const [filter, setFilter] = useState("");

  // Prescreening invites
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    invited_email: "",
    role_hint: "engineer",  // legacy backend field — no longer surfaced in UI; retained so the invite payload validates
    note: "Hi — thanks for the conversation. Please complete your prescreening profile using the link below so we can add you to the SoverenX talent pool. It takes few minutes of your time.\n\nSoverenX Systems only offers casual and contract engagements at this time. Full-time employment is not available.\n\nReach out if you have any questions before submitting.",
  });

  // Bulk prescreening invites
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    emails_raw: "",
    role_hint: "engineer",
    note: "Hi — thanks for the conversation. Please complete your prescreening profile using the link below so we can add you to the SoverenX talent pool. It takes few minutes of your time.\n\nSoverenX Systems only offers casual and contract engagements at this time. Full-time employment is not available.\n\nReach out if you have any questions before submitting.",
  });
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Manual reminders (prescreening + doc-screening)
  const [reminderType, setReminderType] = useState(null);  // null | 'prescreening' | 'docs'
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  // Approval review modal
  const [reviewing, setReviewing] = useState(null);

  function init() {
    return {
      name: "", email: "", role_title: "",
      person_type: ["engineer"], status: "available", utilization: 0,
      day_rate: 700, hourly_rate: 0, region: "",
      address: "", contact_phone: "", national_id: "", abn_number: "",
      vendors: [], skills: [], certifications: [], notes: "",
    };
  }

  const load = async () => {
    const [p, c, inv] = await Promise.all([
      api.get("/personnel"),
      api.get("/certifications"),
      api.get("/personnel-invites"),
    ]);
    setPeople(p.data); setCertCatalog(c.data); setInvites(inv.data);
  };
  useEffect(() => { load(); }, []);

  // ---- Prescreening invite helpers ----
  const sendInvite = async () => {
    if (!inviteForm.invited_email) { toast.error("Email required"); return; }
    if (!inviteForm.note || inviteForm.note.trim().length < 20) { toast.error("Please write a short note to the candidate (at least 20 characters)"); return; }
    try {
      await api.post("/personnel-invites", inviteForm);
      toast.success(`Prescreening invite sent to ${inviteForm.invited_email}`);
      setInviteOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invite"); }
  };
  const sendBulkInvites = async () => {
    const raw = bulkForm.emails_raw || "";
    // Accept one per line (also tolerate commas/semicolons as safety nets)
    const emails = raw.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Paste at least one email"); return; }
    if (emails.length > 100) { toast.error("Maximum 100 emails per batch"); return; }
    if (!bulkForm.note || bulkForm.note.trim().length < 20) { toast.error("Note must be at least 20 characters"); return; }
    setBulkSending(true);
    setBulkResult(null);
    try {
      const r = await api.post("/personnel-invites/bulk", {
        emails,
        role_hint: bulkForm.role_hint,
        note: bulkForm.note,
      });
      setBulkResult(r.data);
      if (r.data.sent_count > 0) toast.success(`${r.data.sent_count} invite${r.data.sent_count === 1 ? "" : "s"} sent`);
      if (r.data.skipped_count > 0) toast.info(`${r.data.skipped_count} skipped`);
      if (r.data.failed_count > 0) toast.warning(`${r.data.failed_count} failed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally { setBulkSending(false); }
  };

  const sendManualReminder = async () => {
    const email = (reminderEmail || "").trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    const path = reminderType === "docs" ? "docs-screening" : "prescreening";
    setReminderBusy(true);
    setReminderResult(null);
    try {
      const r = await api.post(`/personnel-invites/reminders/${path}`, { email });
      setReminderResult({ ok: true, ...r.data });
      if (r.data?.sent) toast.success(`${reminderType === "docs" ? "Doc-screening" : "Prescreening"} reminder sent`);
      else toast.warning("Reminder recorded but email failed to deliver");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to send reminder";
      setReminderResult({ ok: false, error: detail });
      if (e.response?.status === 429) toast.info(detail);
      else toast.error(detail);
    } finally { setReminderBusy(false); }
  };
  const openReminder = (type) => {
    setReminderType(type);
    setReminderEmail("");
    setReminderResult(null);
  };
  const revokeInvite = async (iid) => {
    if (!window.confirm("Revoke this prescreening invite?")) return;
    try { await api.delete(`/personnel-invites/${iid}`); toast.success("Revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };
  const approveInvite = async (iid) => {
    try {
      const r = await api.post(`/personnel-invites/${iid}/approve`);
      toast.success(`Approved — application ${r.data.applicant_id || ""} moved to Document Screening`);
      setReviewing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  // Rejection now REQUIRES an internal note before we hit the backend
  const [rejectingInvite, setRejectingInvite] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const openRejectDialog = (inv) => { setRejectingInvite(inv); setRejectNote(""); };
  const confirmReject = async () => {
    if (!rejectNote || rejectNote.trim().length < 5) { toast.error("Please add an internal rejection note (min 5 chars)"); return; }
    try {
      await api.post(`/personnel-invites/${rejectingInvite.id}/reject`, { note: rejectNote.trim() });
      toast.success("Rejected — candidate has been emailed a graceful message");
      setRejectingInvite(null); setRejectNote(""); setReviewing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  // Hold (Pending) — parks the application without rejecting
  const holdInvite = async (iid) => {
    const note = window.prompt("Optional internal note about why you're holding this application:", "") || "";
    try {
      await api.post(`/personnel-invites/${iid}/hold`, { note });
      toast.success("Application placed on hold");
      setReviewing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hold failed"); }
  };

  const [editNote, setEditNote] = useState("");
  const [showEditNote, setShowEditNote] = useState(false);
  const requestEdit = async (iid) => {
    try {
      await api.post(`/personnel-invites/${iid}/request-edit`, { note: editNote });
      toast.success("Edit request sent — candidate can now update their submission");
      setShowEditNote(false); setEditNote(""); setReviewing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Manual agreement tracking — the auto-generated contractor-agreement +
  // in-app signing flow has been retired. Admins send the Employment Contract
  // + Offer Letter from their own Outlook mailbox and tick these two flags
  // once each step happens offline.
  const [agreeBusy, setAgreeBusy] = useState(false);
  const markAgreementSent = async (iid, value) => {
    const action = value ? "mark the Employment Contract as sent" : "undo the \"sent\" flag";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    setAgreeBusy(true);
    try {
      await api.post(`/personnel-invites/${iid}/mark-agreement-sent`, { value });
      toast.success(value ? "Marked as sent" : "Sent flag cleared");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAgreeBusy(false); }
  };
  const markContractSigned = async (iid, value) => {
    const action = value ? "mark the Employment Contract as signed by the candidate" : "undo the \"signed\" flag";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    setAgreeBusy(true);
    try {
      await api.post(`/personnel-invites/${iid}/mark-contract-signed`, { value });
      toast.success(value ? "Marked as signed" : "Signed flag cleared");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAgreeBusy(false); }
  };

  const [offerOpen, setOfferOpen] = useState(null); // holds invite
  const [offerForm, setOfferForm] = useState({ hourly_rate: 0, hours_per_week: 20, start_date: "", project_ref: "", notes: "" });
  const openOfferDialog = (inv) => {
    setOfferForm({
      hourly_rate: inv.agreement?.hourly_rate || inv.submission?.hourly_rate || 0,
      hours_per_week: inv.submission?.weekly_hours_committed || 20,
      start_date: "",
      project_ref: "",
      notes: "",
    });
    setOfferOpen(inv);
  };
  const sendOffer = async () => {
    try {
      await api.post(`/personnel-invites/${offerOpen.id}/generate-offer`, offerForm);
      toast.success("Offer letter generated and emailed to candidate");
      setOfferOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [agreementView, setAgreementView] = useState(null);
  const viewAgreement = async (iid) => {
    try {
      const r = await api.get(`/personnel-invites/${iid}/agreement`);
      setAgreementView(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load agreement"); }
  };

  const requestAvailability = async (pid, name) => {
    if (!window.confirm(`Send ${name} a fresh Availability Portal link via email?\n\nThey will get a private link to update their next 1-4 weeks of availability without logging in.`)) return;
    try {
      const r = await api.post(`/personnel/${pid}/availability-link`);
      try { await navigator.clipboard.writeText(r.data.portal_url); toast.success(`Link emailed to ${name} + copied to clipboard`); }
      catch { toast.success(`Availability link sent to ${name}`); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelAgreement = async (iid) => {
    const reason = window.prompt("Reason for cancelling this contract (visible in the audit trail):", "Terms need to be renegotiated");
    if (reason === null) return;
    try {
      await api.post(`/personnel-invites/${iid}/cancel-agreement`, { reason: reason || "Cancelled by admin" });
      toast.success(`Agreement cancelled at ${new Date().toLocaleString()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clearWorkflow = async (iid, name) => {
    if (!window.confirm(`Clear the entire contract workflow for ${name}?\n\nThis removes the current agreement and offer letter so you can restart the process with fresh terms.\n\nThe personnel record itself is NOT deleted.`)) return;
    try {
      await api.post(`/personnel-invites/${iid}/clear-workflow`);
      toast.success("Workflow cleared — you can now generate a new agreement");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeApprovedInvite = async (iid, name) => {
    if (!window.confirm(`Remove ${name} from the approved candidates list?\n\nThis deletes the prescreening invite record entirely. The linked personnel record on the roster stays untouched — delete that separately from the table below if needed.`)) return;
    try {
      await api.delete(`/personnel-invites/${iid}`);
      toast.success("Invite removed from approved list");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleDocsReceived = async (i) => {
    const currentlyReceived = !!i.docs_received;
    let note = i.docs_note || "";
    if (!currentlyReceived) {
      note = window.prompt("Docs received via email to info@soverenxsystems.com.au — optional note (e.g. subject line used, or docs missing):", note) || "";
    } else {
      if (!window.confirm(`Un-mark docs received for ${i.submission?.name || i.invited_email}? This will revert the doc-screening state to pending.`)) return;
    }
    try {
      await api.post(`/personnel-invites/${i.id}/docs-received`, { received: !currentlyReceived, note });
      toast.success(!currentlyReceived ? "Marked as docs received — you can now generate the agreement" : "Reverted docs status to pending");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---- Timesheet portal link helpers ----
  const genTimesheetLink = async (personId, personName) => {
    try {
      const r = await api.post(`/personnel/${personId}/timesheet-link`);
      const url = `${window.location.origin}/portal/timesheet/${r.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Timesheet link for ${personName} copied to clipboard`);
      load();
    } catch { toast.error("Could not generate link"); }
  };
  const copyTimesheetLink = async (token, personName) => {
    const url = `${window.location.origin}/portal/timesheet/${token}`;
    await navigator.clipboard.writeText(url);
    toast.success(`${personName}'s timesheet link copied`);
  };
  const revokeTimesheetLink = async (personId, personName) => {
    if (!window.confirm(`Revoke timesheet portal link for ${personName}? They will no longer be able to log hours until you issue a new link.`)) return;
    try { await api.delete(`/personnel/${personId}/timesheet-link`); toast.success("Link revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };

  const pendingReview = invites.filter((i) => i.status === "submitted");
  const heldInvites = invites.filter((i) => i.status === "held");
  const rejectedInvites = invites.filter((i) => i.status === "rejected");
  const [showRejected, setShowRejected] = useState(false);

  const openCreate = () => { setEditing(null); setForm(init()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...init(), ...p, certifications: p.certifications || [] }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/personnel/${editing.id}`, form);
      else await api.post("/personnel", form);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!isAdmin) { toast.error("Admin only"); return; }
    if (!window.confirm("Delete this person?")) return;
    try { await api.delete(`/personnel/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed (admin only)"); }
  };
  const deleteInvite = async (inv) => {
    if (!isAdmin) { toast.error("Admin only"); return; }
    const label = inv.submission?.name || inv.invited_email;
    const extra = inv.approved_personnel_id ? "\n\nNote: this candidate was approved — their personnel record will be PRESERVED. Only the invite record will be deleted." : "";
    if (!window.confirm(`Delete invite for ${label} (${inv.applicant_id || "no applicant id"})?${extra}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/personnel-invites/${inv.id}`);
      toast.success(`Invite for ${label} deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const editApplicantId = async (inv) => {
    if (!isAdmin) { toast.error("Admin only"); return; }
    const current = inv.applicant_id || "SVX-";
    const next = window.prompt(
      `Edit Applicant ID for ${inv.submission?.name || inv.invited_email}.\n\nFormat: SVX-XXXXXX (uppercase letters/digits, 3–16 chars after the hyphen).\nMust be unique across all invites.`,
      current,
    );
    if (!next || next === current) return;
    const nextUpper = next.toUpperCase();
    try {
      const r = await api.patch(`/personnel-invites/${inv.id}/applicant-id`, { applicant_id: nextUpper });
      toast.success(`Applicant ID → ${r.data.applicant_id}`);
      load();
    } catch (e) {
      // Handle 409 with replace option (detail is an object)
      const detail = e.response?.data?.detail;
      const isClash = e.response?.status === 409 && typeof detail === "object" && detail?.can_replace;
      if (isClash) {
        const dateLabel = detail.clash_created_at ? ` (invited ${detail.clash_created_at.slice(0,10)})` : "";
        const statusLabel = detail.clash_status ? ` [${detail.clash_status}]` : "";
        const ok = window.confirm(
          `Applicant ID ${nextUpper} is currently held by a PREVIOUS entry:\n→ ${detail.clash_email}${statusLabel}${dateLabel}\n\nReplace the previous entry? This will:\n  • Delete that previous invite record\n  • Assign the ID to this invite\n\nThis cannot be undone.`
        );
        if (!ok) return;
        try {
          const r2 = await api.patch(`/personnel-invites/${inv.id}/applicant-id`, { applicant_id: nextUpper, replace_existing: true });
          const extra = r2.data.preserved_personnel_id ? " · personnel record preserved" : "";
          toast.success(`Applicant ID → ${r2.data.applicant_id} · previous entry deleted${extra}`);
          load();
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Replace failed");
          return;
        }
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Update failed"));
    }
  };

  const toggleArr = (key, v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const addCert = () => setForm((f) => ({ ...f, certifications: [...f.certifications, { vendor: "Cisco", code: "", tier: "", issued_date: "", expiry_date: "", proof_file_id: null }] }));
  const updateCert = (i, patch) => setForm((f) => ({ ...f, certifications: f.certifications.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const removeCert = (i) => setForm((f) => ({ ...f, certifications: f.certifications.filter((_, idx) => idx !== i) }));

  const uploadProof = async (personId, cert_code, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/personnel/${personId}/upload-cert-proof?cert_code=${encodeURIComponent(cert_code)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Proof uploaded");
      // Refresh editing form
      const fresh = (await api.get(`/personnel/${personId}`)).data;
      setForm({ ...init(), ...fresh });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const filtered = people.filter((p) => {
    const q = filter.toLowerCase();
    if (!q) return true;
    return p.name.toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q) || (p.role_title || "").toLowerCase().includes(q) || (p.region || "").toLowerCase().includes(q);
  });

  return (
    <div>
      <PageHeader
        overline="HR · Personnel Master"
        title="Personnel Records"
        subtitle="One record per person. Edits here flow into Talent Pool and Field Technicians automatically."
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button data-testid="send-onboard-btn" variant="outline" className="border-slate-300 text-[#0F1E38] hover:bg-slate-50 font-mono uppercase tracking-wider text-xs">
                  <Envelope size={14} className="mr-2" /> Send Prescreening Link
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-md">
                <DialogHeader><DialogTitle>Send Prescreening Link</DialogTitle></DialogHeader>
                <div className="text-xs text-slate-500 mb-4 leading-relaxed">
                  A public link will be emailed to the candidate. They fill in their own prescreening details — no account needed. You review and approve before they move to document screening.
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Candidate Email</Label>
                    <Input data-testid="onb-email" type="email" value={inviteForm.invited_email} onChange={(e) => setInviteForm({ ...inviteForm, invited_email: e.target.value })} placeholder="new-hire@example.com" className="mt-1" />
                  </div>
                  <div>
                    <Label>Note to Candidate <span className="text-red-500">*required</span></Label>
                    <Textarea rows={5} data-testid="onb-note" value={inviteForm.note} onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })} placeholder="Personalise this note for the candidate…" className="mt-1" />
                    <div className="text-[10px] text-slate-500 mt-1">Prefilled with a friendly template — feel free to edit. Minimum 20 characters.</div>
                  </div>
                  <div className="text-[11px] text-slate-500 pt-1">Link expires in 14 days. Single-use — candidate can only submit once.</div>
                </div>
                <DialogFooter><Button data-testid="onb-send" onClick={sendInvite} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">Send Invite</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) setBulkResult(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-invite-btn" variant="outline" className="border-slate-300 text-[#0F1E38] hover:bg-slate-50 font-mono uppercase tracking-wider text-xs">
                  <Envelope size={14} className="mr-2" /> Bulk Invite
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-xl">
                <DialogHeader><DialogTitle>Bulk Prescreening Invites</DialogTitle></DialogHeader>
                <div className="text-xs text-slate-500 mb-4 leading-relaxed">
                  Paste up to 100 candidate emails (one per line). Each gets the same note and role hint. Candidates already with an active invite are skipped automatically.
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Candidate Emails <span className="text-slate-500">(one per line, max 100)</span></Label>
                    <Textarea
                      data-testid="bulk-emails"
                      rows={7}
                      value={bulkForm.emails_raw}
                      onChange={(e) => setBulkForm({ ...bulkForm, emails_raw: e.target.value })}
                      placeholder={"alice@example.com\nbob@example.com\ncarol@example.com"}
                      className="mt-1 font-mono text-xs"
                    />
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">
                      {(bulkForm.emails_raw.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).length)} email(s) ready
                    </div>
                  </div>
                  <div>
                    <Label>Role Hint</Label>
                    <select
                      data-testid="bulk-role"
                      value={bulkForm.role_hint}
                      onChange={(e) => setBulkForm({ ...bulkForm, role_hint: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded border border-slate-300 bg-white text-sm font-mono"
                    >
                      <option value="engineer">Engineer</option>
                      <option value="field_tech">Field Technician</option>
                    </select>
                  </div>
                  <div>
                    <Label>Note to Candidates <span className="text-red-500">*required</span></Label>
                    <Textarea data-testid="bulk-note" rows={5} value={bulkForm.note} onChange={(e) => setBulkForm({ ...bulkForm, note: e.target.value })} className="mt-1" />
                    <div className="text-[10px] text-slate-500 mt-1">Same note goes to every candidate in this batch. Minimum 20 characters.</div>
                  </div>
                  <div className="text-[11px] text-slate-500">Links expire in 14 days. Each candidate can only submit once.</div>

                  {bulkResult && (
                    <div data-testid="bulk-result" className="mt-3 border border-slate-200 rounded-md overflow-hidden text-xs">
                      <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
                        <div className="px-3 py-2 border-r border-slate-200"><div className="text-[10px] font-mono uppercase text-slate-500">Sent</div><div className="text-lg font-semibold text-emerald-700">{bulkResult.sent_count}</div></div>
                        <div className="px-3 py-2 border-r border-slate-200"><div className="text-[10px] font-mono uppercase text-slate-500">Skipped</div><div className="text-lg font-semibold text-amber-700">{bulkResult.skipped_count}</div></div>
                        <div className="px-3 py-2"><div className="text-[10px] font-mono uppercase text-slate-500">Failed</div><div className="text-lg font-semibold text-rose-700">{bulkResult.failed_count}</div></div>
                      </div>
                      {(bulkResult.skipped?.length > 0 || bulkResult.failed?.length > 0) && (
                        <div className="max-h-48 overflow-y-auto bg-white">
                          {bulkResult.skipped?.map((s, i) => (
                            <div key={`sk-${i}`} className="px-3 py-1.5 border-b border-slate-100 flex justify-between gap-2">
                              <span className="font-mono text-[11px] text-[#0F1E38]">{s.email}</span>
                              <span className="text-[10px] font-mono text-amber-700 uppercase">skipped · {s.reason}</span>
                            </div>
                          ))}
                          {bulkResult.failed?.map((f, i) => (
                            <div key={`fl-${i}`} className="px-3 py-1.5 border-b border-slate-100 flex justify-between gap-2 bg-rose-50/50">
                              <span className="font-mono text-[11px] text-[#0F1E38]">{f.email}</span>
                              <span className="text-[10px] font-mono text-rose-700 uppercase">failed · {f.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button data-testid="bulk-send" onClick={sendBulkInvites} disabled={bulkSending} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">
                    {bulkSending ? "Sending…" : "Send Batch"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button
              data-testid="send-prescreening-reminder-btn"
              variant="outline"
              className="border-slate-300 text-[#0F1E38] hover:bg-slate-50 font-mono uppercase tracking-wider text-xs"
              onClick={() => openReminder("prescreening")}
            >
              <ClockCounterClockwise size={14} className="mr-2" /> Prescreening Reminder
            </Button>
            <Button
              data-testid="send-docs-reminder-btn"
              variant="outline"
              className="border-slate-300 text-[#0F1E38] hover:bg-slate-50 font-mono uppercase tracking-wider text-xs"
              onClick={() => openReminder("docs")}
            >
              <ClockCounterClockwise size={14} className="mr-2" /> Doc-Screening Reminder
            </Button>
            <Dialog open={!!reminderType} onOpenChange={(v) => { if (!v) { setReminderType(null); setReminderResult(null); } }}>
              <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    Send {reminderType === "docs" ? "Doc-Screening" : "Prescreening"} Reminder
                  </DialogTitle>
                </DialogHeader>
                <div className="text-xs text-slate-600 mb-4 leading-relaxed">
                  {reminderType === "docs"
                    ? "Enter the email of an approved candidate who hasn't sent their documents yet. The system will look up their applicant ID and resend the Document Screening checklist."
                    : "Enter the email of a candidate with an open prescreening invite. The system will resend their onboarding link."}
                  <div className="mt-1 text-[10px] font-mono text-amber-700">Rate-limited: max 1 reminder per candidate per 24h.</div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Candidate Email</Label>
                    <Input
                      data-testid="reminder-email-input"
                      type="email"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                      placeholder="candidate@example.com"
                      className="mt-1 font-mono"
                    />
                  </div>
                  {reminderResult && (
                    <div data-testid="reminder-result" className={`p-3 rounded text-xs font-mono ${reminderResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
                      {reminderResult.ok
                        ? <>✓ Reminder sent to <strong>{reminderResult.email}</strong>{reminderResult.applicant_id ? ` · ${reminderResult.applicant_id}` : ""} at {fmtDateTime(reminderResult.reminder_sent_at)}. Total reminders: {reminderResult.reminders_count}</>
                        : <>⚠ {reminderResult.error}</>}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    data-testid="reminder-send-btn"
                    onClick={sendManualReminder}
                    disabled={reminderBusy}
                    className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold"
                  >
                    {reminderBusy ? "Sending…" : "Send Reminder"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>{null}</DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name}` : "New Personnel Record"}</DialogTitle></DialogHeader>

              {/* Basic */}
              <section className="pt-2">
                <div className="overline mb-3">Identity</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Full Name</Label><Input data-testid="p-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                  <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                  <div><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>Region / Base</Label><Input value={form.region} onChange={(e) => setForm({...form, region: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                  <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                  <div><Label>National ID</Label><Input value={form.national_id} onChange={(e) => setForm({...form, national_id: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>ABN / VAT / Company # <span className="text-slate-500">(subcontractors)</span></Label><Input value={form.abn_number} onChange={(e) => setForm({...form, abn_number: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                </div>
              </section>

              {/* Role & Rates */}
              <section className="pt-4">
                <div className="overline mb-3">Role & Rates</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Job Title</Label><Input value={form.role_title} onChange={(e) => setForm({...form, role_title: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Engineer Day Rate ($)</Label><Input type="number" value={form.day_rate} onChange={(e) => setForm({...form, day_rate: parseFloat(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>Field Tech Hourly Rate ($)</Label><Input type="number" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: parseFloat(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>Utilization (%)</Label><Input type="number" value={form.utilization} onChange={(e) => setForm({...form, utilization: parseInt(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div>
                    <Label>Type <span className="text-slate-500">(drives which tab they appear in)</span></Label>
                    <div className="flex gap-2 mt-2">
                      {PERSON_TYPES.map((t) => (
                        <button key={t.key} type="button" data-testid={`type-${t.key}`} onClick={() => toggleArr("person_type", t.key)}
                          className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider border ${form.person_type.includes(t.key) ? "bg-[#FFC107]/20 border-[#FFC107] text-[#0F1E38]" : "border-slate-300 text-slate-500"}`}>{t.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Vendors / Skills */}
              <section className="pt-4">
                <div className="overline mb-3">Vendors & Skills</div>
                <Label>Vendors</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {VENDORS.map((v) => <button key={v} type="button" onClick={() => toggleArr("vendors", v)} className={`px-3 py-1 rounded text-xs font-mono uppercase border ${form.vendors.includes(v) ? "bg-[#FFC107]/20 border-[#FFC107] text-[#0F1E38]" : "border-slate-300 text-slate-500"}`}>{v}</button>)}
                </div>
                <Label className="mt-4 block">Field Tech Skills</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SKILL_LIB.map((s) => <button key={s} type="button" onClick={() => toggleArr("skills", s)} className={`px-2.5 py-1 rounded text-[11px] border ${form.skills.includes(s) ? "bg-violet-500/15 border-violet-500/50 text-violet-300" : "border-slate-300 text-slate-500"}`}>{s}</button>)}
                </div>
              </section>

              {/* Certifications */}
              <section className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Certifications</div>
                  <Button data-testid="add-cert-btn" size="sm" onClick={addCert} className="h-7 text-xs bg-[#FFC107] hover:bg-[#E5A906]"><Plus size={12} className="mr-1" /> Add Cert</Button>
                </div>
                <div className="space-y-2">
                  {form.certifications.length === 0 && <div className="text-xs text-slate-500 font-mono">No certifications recorded.</div>}
                  {form.certifications.map((c, i) => (
                    <div key={i} data-testid={`cert-row-${i}`} className="border border-slate-200 rounded p-3 bg-slate-50">
                      <div className="grid grid-cols-6 gap-2 items-end">
                        <div className="col-span-1">
                          <Label className="text-[10px]">Vendor</Label>
                          <Select value={c.vendor} onValueChange={(v) => updateCert(i, { vendor: v })}>
                            <SelectTrigger className="bg-slate-50 border-slate-300 h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{VENDORS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px]">Cert Code</Label>
                          <Input value={c.code} onChange={(e) => updateCert(i, { code: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1 font-mono" placeholder="CCIE"/>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px]">Tier</Label>
                          <Input value={c.tier || ""} onChange={(e) => updateCert(i, { tier: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1"/>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px]">Issued</Label>
                          <Input type="date" value={c.issued_date || ""} onChange={(e) => updateCert(i, { issued_date: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1 font-mono"/>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px]">Expiry</Label>
                          <Input type="date" value={c.expiry_date || ""} onChange={(e) => updateCert(i, { expiry_date: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1 font-mono"/>
                        </div>
                        <div className="col-span-1 flex items-center gap-1">
                          {editing && c.code && (
                            <label className="flex-1 h-8 px-2 flex items-center justify-center text-[10px] font-mono uppercase border border-slate-300 rounded cursor-pointer hover:bg-slate-100" style={{transition:"background-color 0.15s ease"}}>
                              <UploadSimple size={12} className="mr-1" /> {c.proof_file_id ? "Replace" : "Proof"}
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadProof(editing.id, c.code, e.target.files[0])} />
                            </label>
                          )}
                          <button type="button" data-testid={`cert-remove-${i}`} onClick={() => removeCert(i)} className="text-slate-500 hover:text-red-400 p-1" style={{transition:"color 0.15s ease"}}><X size={14} /></button>
                        </div>
                      </div>
                      {c.proof_file_id && (
                        <a href={`${API}/files/${c.proof_file_id}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1 text-[11px] text-[#0F1E38] hover:text-[#0F1E38] font-mono" style={{transition:"color 0.15s ease"}}>
                          <FileText size={12} /> Proof attached — view
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Notes */}
              <section className="pt-4">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/>
              </section>

              <DialogFooter><Button data-testid="save-person-btn" onClick={save} className="bg-[#FFC107] hover:bg-[#E5A906]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Approved invites — agreement + offer letter workflow */}
      {invites.filter((i) => i.status === "approved").length > 0 && (
        <div className="mb-6 border border-emerald-200 bg-emerald-50/30 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2">
            <span className="text-sm font-semibold text-[#0F1E38]">Approved candidates — contract workflow</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {invites.filter((i) => i.status === "approved").map((i) => {
                const agStatus = i.agreement?.status || null;
                const offerSent = !!i.offer?.sent_at;
                const docsReceived = !!i.docs_received;
                return (
                  <tr key={i.id} data-testid={`approved-invite-${i.id}`} className="border-b border-emerald-200 last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F1E38]">{i.submission?.name || i.invited_email}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {i.invited_email}
                        {i.applicant_id && (
                          isAdmin
                            ? <button data-testid={`edit-aid-${i.id}`} onClick={() => editApplicantId(i)} title="Edit Applicant ID (admin only)" className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[#0F1E38] hover:bg-amber-50 hover:border-amber-300 cursor-pointer">{i.applicant_id} <PencilSimple size={9} className="inline -mt-0.5 opacity-60" /></button>
                            : <span className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[#0F1E38]">{i.applicant_id}</span>
                        )}
                        {!i.applicant_id && isAdmin && <button data-testid={`set-aid-${i.id}`} onClick={() => editApplicantId(i)} title="Set Applicant ID (admin only)" className="ml-2 px-1.5 py-0.5 rounded border border-dashed border-slate-300 text-slate-500 hover:text-amber-700 hover:border-amber-300">+ ID</button>}
                        {i.submission?.country && <span className="ml-2 text-slate-400">· {i.submission.country}</span>}
                        {isAdmin && <button data-testid={`del-inv-${i.id}`} onClick={() => deleteInvite(i)} title="Delete invite record (admin only)" className="ml-2 text-slate-400 hover:text-rose-600"><Trash size={11} /></button>}
                      </div>
                      <CompactTimeline invite={i} />
                    </td>
                    <td className="text-xs whitespace-nowrap px-2">
                      {docsReceived
                        ? <button data-testid={`docs-toggle-${i.id}`} onClick={() => toggleDocsReceived(i)} className="text-emerald-700 font-mono text-[11px] hover:underline" title={`Marked by ${i.docs_received_by} on ${i.docs_received_at?.slice(0,10)}${i.docs_note ? ' — ' + i.docs_note : ''}`}>✓ Docs received {i.docs_received_at?.slice(0,10)}</button>
                        : <button data-testid={`docs-toggle-${i.id}`} onClick={() => toggleDocsReceived(i)} className="px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 font-mono text-[11px] hover:bg-amber-100">Mark docs received</button>
                      }
                    </td>
                    <td className="text-xs text-slate-500 whitespace-nowrap">
                      {agStatus === "signed" ? <span className="text-emerald-700 font-mono">✓ Agreement signed {i.agreement?.signed_at?.slice(0,10)}</span> :
                       agStatus === "sent" ? <span className="text-amber-700 font-mono">⧗ Agreement sent — awaiting signature</span> :
                       agStatus === "cancelled" ? <span className="text-red-700 font-mono" title={i.agreement?.cancelled_reason}>⊘ Cancelled {i.agreement?.cancelled_at?.slice(0,10)}</span> :
                       <span className="text-slate-500 font-mono">— No agreement yet</span>}
                    </td>
                    <td className="text-xs text-slate-500 whitespace-nowrap">
                      {offerSent ? <span className="text-emerald-700 font-mono">✓ Offer sent</span> : <span className="text-slate-400 font-mono">—</span>}
                    </td>
                    <td className="pr-4 py-3 text-right">
                      <div className="flex gap-2 justify-end flex-wrap">
                        {/* Manual Employment Contract tracking — no auto-generation, no in-app signing.
                            Admin sends the contract from Outlook, then ticks these flags. */}
                        {!i.agreement_sent_manually ? (
                          <Button size="sm" data-testid={`mark-agreement-sent-${i.id}`}
                            onClick={() => markAgreementSent(i.id, true)}
                            disabled={agreeBusy}
                            className={`h-8 text-xs text-white ${docsReceived ? 'bg-[#0F1E38] hover:bg-[#1E3A5F]' : 'bg-slate-400 hover:bg-slate-500'}`}
                            title={docsReceived ? 'Mark that you have emailed the Employment Contract + Offer Letter from Outlook' : 'Mark docs received first (recommended)'}>
                            Mark Agreement Sent
                          </Button>
                        ) : (
                          <>
                            <button data-testid={`agreement-sent-badge-${i.id}`} onClick={() => markAgreementSent(i.id, false)}
                              title={`Sent manually on ${i.agreement_sent_manually_at?.slice(0,10)} by ${i.agreement_sent_manually_by || 'admin'} — click to undo`}
                              className="text-emerald-700 font-mono text-[11px] hover:underline">✓ Agreement sent {i.agreement_sent_manually_at?.slice(0,10)}</button>
                            {!i.contract_signed_manually ? (
                              <Button size="sm" data-testid={`mark-contract-signed-${i.id}`}
                                onClick={() => markContractSigned(i.id, true)}
                                disabled={agreeBusy}
                                className="h-8 text-xs bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38]">
                                Mark Contract Signed
                              </Button>
                            ) : (
                              <button data-testid={`contract-signed-badge-${i.id}`} onClick={() => markContractSigned(i.id, false)}
                                title={`Signed on ${i.contract_signed_manually_at?.slice(0,10)} (marked by ${i.contract_signed_manually_by || 'admin'}) — click to undo`}
                                className="text-emerald-600 font-mono text-[11px] font-bold hover:underline">✓ Contract signed {i.contract_signed_manually_at?.slice(0,10)}</button>
                            )}
                          </>
                        )}
                        <Button size="sm" variant="outline" data-testid={`remove-approved-${i.id}`} onClick={() => removeApprovedInvite(i.id, i.submission?.name || i.invited_email)} className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50" title="Remove this approved candidate from the workflow list entirely">Remove</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* View Signed Agreement dialog */}
      <Dialog open={!!agreementView} onOpenChange={(v) => !v && setAgreementView(null)}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Signed Contractor Agreement — {agreementView?.candidate_name}</DialogTitle></DialogHeader>
          {agreementView && (
            <>
              {agreementView.signature && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-800 mb-1 font-bold">Signature Audit Trail</div>
                  <div>Signed by: <strong>{agreementView.signature.name}</strong></div>
                  <div>Signed at: {agreementView.signature.signed_at}</div>
                  <div>IP: {agreementView.signature.ip_address}</div>
                  <div className="truncate">Browser: {agreementView.signature.user_agent}</div>
                </div>
              )}
              {agreementView.status !== "signed" && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">Awaiting candidate signature.</div>
              )}
              <div className="border border-slate-200 rounded p-4 bg-slate-50" dangerouslySetInnerHTML={{ __html: agreementView.html }} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Offer letter dialog */}
      <Dialog open={!!offerOpen} onOpenChange={(v) => !v && setOfferOpen(null)}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg">
          <DialogHeader><DialogTitle>Offer Letter — {offerOpen?.submission?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-4">Adjust hours and rate before sending. Candidate will receive the offer as a formatted email.</div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hourly Rate (AUD)</Label><Input data-testid="offer-rate" type="number" min="0" step="1" value={offerForm.hourly_rate} onChange={(e) => setOfferForm({ ...offerForm, hourly_rate: parseFloat(e.target.value) || 0 })} className="mt-1 font-mono" /></div>
              <div><Label>Hours per week</Label><Input data-testid="offer-hpw" type="number" min="0" max="60" value={offerForm.hours_per_week} onChange={(e) => setOfferForm({ ...offerForm, hours_per_week: parseInt(e.target.value) || 0 })} className="mt-1 font-mono" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={offerForm.start_date} onChange={(e) => setOfferForm({ ...offerForm, start_date: e.target.value })} className="mt-1 font-mono" /></div>
              <div><Label>Project Reference <span className="text-slate-400">(optional)</span></Label><Input value={offerForm.project_ref} onChange={(e) => setOfferForm({ ...offerForm, project_ref: e.target.value })} className="mt-1" placeholder="e.g. Blackstone SD-WAN" /></div>
            </div>
            <div><Label>Additional Notes <span className="text-slate-400">(optional)</span></Label><Textarea rows={3} value={offerForm.notes} onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })} className="mt-1" placeholder="Any specific engagement notes for the candidate…" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="send-offer-btn" onClick={sendOffer} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">Send Offer Letter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescreening — awaiting review */}
      {pendingReview.length > 0 && (
        <div className="mb-6 border border-amber-200 bg-amber-50/50 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={16} className="text-amber-700" weight="bold" />
              <span className="text-sm font-semibold text-[#0F1E38]">Prescreening — awaiting review</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">{pendingReview.length}</span>
            </div>
            <span className="text-[11px] text-slate-500">Review submissions and Approve · Hold · Reject</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {pendingReview.map((i) => (
                <tr key={i.id} data-testid={`pending-invite-${i.id}`} className="border-b border-amber-200 last:border-b-0 hover:bg-amber-50/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1E38]">{i.submission?.name || i.invited_email}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {i.invited_email} · {i.role_hint === "field_tech" ? "Field Tech" : "Engineer"}
                      {i.applicant_id && (
                        isAdmin
                          ? <button data-testid={`edit-aid-${i.id}`} onClick={() => editApplicantId(i)} title="Edit Applicant ID (admin only)" className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[#0F1E38] hover:bg-amber-50 hover:border-amber-300 cursor-pointer">{i.applicant_id} <PencilSimple size={9} className="inline -mt-0.5 opacity-60" /></button>
                          : <span className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[#0F1E38]">{i.applicant_id}</span>
                      )}
                      {isAdmin && <button data-testid={`del-inv-${i.id}`} onClick={() => deleteInvite(i)} title="Delete invite record (admin only)" className="ml-2 text-slate-400 hover:text-rose-600"><Trash size={11} /></button>}
                    </div>
                    <CompactTimeline invite={i} />
                  </td>
                  <td className="text-xs text-slate-500">Submitted {i.submitted_at?.slice(0, 10)}</td>
                  <td className="pr-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" data-testid={`review-${i.id}`} onClick={() => setReviewing(i)} variant="outline" className="h-8 text-xs border-slate-300">Review</Button>
                      <Button size="sm" data-testid={`approve-${i.id}`} onClick={() => approveInvite(i.id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                      <Button size="sm" data-testid={`hold-${i.id}`} onClick={() => holdInvite(i.id)} variant="outline" className="h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-50">Hold</Button>
                      <Button size="sm" data-testid={`reject-${i.id}`} onClick={() => openRejectDialog(i)} variant="outline" className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* On Hold — parked applications */}
      {heldInvites.length > 0 && (
        <div className="mb-6 border border-slate-200 bg-slate-50/60 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={16} className="text-slate-600" weight="bold" />
              <span className="text-sm font-semibold text-[#0F1E38]">On hold — parked applications</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-700 bg-white border border-slate-300 rounded px-1.5 py-0.5">{heldInvites.length}</span>
            </div>
            <span className="text-[11px] text-slate-500">Return here later to approve or reject</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {heldInvites.map((i) => (
                <tr key={i.id} data-testid={`held-invite-${i.id}`} className="border-b border-slate-200 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1E38]">{i.submission?.name || i.invited_email}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {i.invited_email}
                      {i.applicant_id && <span className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[#0F1E38]">{i.applicant_id}</span>}
                    </div>
                    {i.hold_note && <div className="text-[11px] text-slate-500 mt-1 italic">Note: {i.hold_note}</div>}
                  </td>
                  <td className="text-xs text-slate-500">Held {i.held_at?.slice(0, 10)}</td>
                  <td className="pr-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" onClick={() => setReviewing(i)} variant="outline" className="h-8 text-xs border-slate-300">Review</Button>
                      <Button size="sm" data-testid={`held-approve-${i.id}`} onClick={() => approveInvite(i.id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                      <Button size="sm" data-testid={`held-reject-${i.id}`} onClick={() => openRejectDialog(i)} variant="outline" className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejected applications — collapsed by default */}
      {rejectedInvites.length > 0 && (
        <div className="mb-6 border border-slate-200 bg-white rounded-lg overflow-hidden">
          <button
            type="button"
            data-testid="toggle-rejected"
            onClick={() => setShowRejected((s) => !s)}
            className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-200 text-left hover:bg-slate-100"
            style={{ transition: "background-color 0.15s ease" }}
          >
            <div className="flex items-center gap-2">
              <WarningCircle size={16} className="text-red-500" weight="bold" />
              <span className="text-sm font-semibold text-[#0F1E38]">Rejected applications</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">{rejectedInvites.length}</span>
            </div>
            <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{showRejected ? "Hide" : "Show"}</span>
          </button>
          {showRejected && (
            <table className="w-full text-sm">
              <tbody>
                {rejectedInvites.map((i) => (
                  <tr key={i.id} data-testid={`rejected-invite-${i.id}`} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F1E38]">{i.submission?.name || i.invited_email}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {i.invited_email}
                        {i.applicant_id && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-50 border border-slate-300 text-[#0F1E38]">{i.applicant_id}</span>}
                      </div>
                      {i.rejection_note && (
                        <div className="text-[11px] text-red-700 mt-1 italic border-l-2 border-red-200 pl-2">
                          Internal note: {i.rejection_note}
                        </div>
                      )}
                    </td>
                    <td className="text-xs text-slate-500 whitespace-nowrap">
                      Rejected {i.rejected_at?.slice(0, 10)}
                      {i.rejected_by && <div className="text-[10px] text-slate-400">by {i.rejected_by}</div>}
                    </td>
                    <td className="pr-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setReviewing(i)} className="h-8 text-xs border-slate-300">View Submission</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(v) => !v && setReviewing(null)}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Review Prescreening — {reviewing?.submission?.name || reviewing?.invited_email}
              {reviewing?.applicant_id && <span className="ml-2 text-xs font-mono text-slate-500">{reviewing.applicant_id}</span>}
            </DialogTitle>
          </DialogHeader>
          {reviewing && (
            <section className="mb-4 pb-4 border-b border-slate-200">
              <div className="overline text-slate-500 mb-3">Lifecycle Timeline</div>
              <FullTimeline invite={reviewing} />
            </section>
          )}
          {reviewing?.submission && (() => {
            const s = reviewing.submission;
            const curSym = { AUD: "A$", INR: "₹", NZD: "NZ$", PHP: "₱", USD: "$" }[s.currency] || "$";
            const curCode = s.currency || "";
            const rateLabel = s.hourly_rate ? `${curSym}${s.hourly_rate}/hr${curCode ? ` (${curCode})` : ""}` : "";
            return (
              <div className="space-y-3 text-sm">
                {[
                  ["Applicant ID", reviewing.applicant_id],
                  ["Full Name", s.name],
                  ["Email", s.email || reviewing.invited_email],
                  ["Contact Phone", s.contact_phone],
                  ["Country", s.country],
                  ["Region", s.region],
                  ["Address", s.address],
                  ["Job Title", s.role_title],
                  ["Type", (s.person_type || []).join(", ")],
                  ["Hourly Rate", rateLabel],
                  ["Rate Flexibility", s.rate_negotiable ? "Open to negotiate" : (s.hourly_rate ? "Fixed — won't negotiate" : "")],
                  ["ABN Status", { have: "Has active ABN", willing_to_get: "Willing to register", no: "Not willing" }[s.abn_status] || ""],
                  ["ABN Number", s.abn_number],
                  ["Tax IDs", (s.tax_ids || []).map(t => `${t.kind}: ${t.value}`).join(" · ")],
                  ["Indemnity Insurance", { have: "Holds current cover", willing_to_get: "Willing to obtain", no: "Not willing" }[s.indemnity_insurance] || ""],
                  ["Own Equipment (laptop/cables/net)", s.has_own_equipment ? "Yes" : "No"],
                  ["Past Contract Experience", s.contract_experience],
                  ["National ID", s.national_id],
                  ["Vendors", (s.vendors || []).join(", ")],
                  ["Skills", (s.skills || []).join(", ")],
                  ["Certifications", (s.certifications || []).map(c => `${c.vendor} ${c.code}${c.expiry_date ? ` (exp ${c.expiry_date})` : ""}`).join(" · ")],
                  ["Notes", s.notes],
                ].filter(([, v]) => v !== undefined && v !== null && String(v).trim().length > 0).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-4 py-2 border-b border-slate-100">
                    <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{k}</div>
                    <div className="col-span-2 text-[#0F1E38]">{v}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            {reviewing?.status !== "rejected" && (
              <>
                <Button variant="outline" onClick={() => openRejectDialog(reviewing)} className="border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                {reviewing?.status !== "held" && (
                  <Button variant="outline" onClick={() => holdInvite(reviewing?.id)} className="border-amber-400 text-amber-700 hover:bg-amber-50">Hold (Pending)</Button>
                )}
                <Button onClick={() => approveInvite(reviewing?.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Approve — send doc request</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject with internal note dialog */}
      <Dialog open={!!rejectingInvite} onOpenChange={(v) => !v && setRejectingInvite(null)}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-md">
          <DialogHeader><DialogTitle>Reject Application — {rejectingInvite?.submission?.name || rejectingInvite?.invited_email}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-3 leading-relaxed">
            Add an <strong>internal</strong> note explaining why you're rejecting. This is stored on the record and is <strong>never shared</strong> with the candidate — they only receive a graceful "won't be progressing" email.
          </div>
          <Label>Internal Rejection Note <span className="text-red-500">*required</span></Label>
          <Textarea
            data-testid="reject-note"
            rows={5}
            autoFocus
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. Rate way outside budget · declined background check · vendor mix doesn't fit current project pipeline"
            className="mt-1"
          />
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setRejectingInvite(null)} className="border-slate-300">Cancel</Button>
            <Button data-testid="confirm-reject" onClick={confirmReject} className="bg-red-600 hover:bg-red-700 text-white">Confirm Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending outbound invites (not yet submitted) */}
      {invites.filter((i) => i.status === "pending").length > 0 && (
        <div className="mb-6 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="font-mono uppercase tracking-wider">Prescreening links awaiting submission:</span>
          {invites.filter((i) => i.status === "pending").map((i) => (
            <span key={i.id} data-testid={`awaiting-${i.id}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-slate-300 bg-white">
              <span className="font-mono text-[10.5px] text-[#0F1E38]">{i.invited_email}</span>
              <button data-testid={`awaiting-revoke-${i.id}`} onClick={() => revokeInvite(i.id)} className="text-slate-400 hover:text-red-500" style={{ transition: "color 0.15s ease" }}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="mb-4">
        <Input data-testid="personnel-search" placeholder="Search name, email, role, region..." value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-md bg-white border-slate-300" />
      </div>

      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-200">
              <th className="px-4 py-3">Person</th><th>Type</th><th>Region</th><th>Vendors / Skills</th><th>Certs</th><th className="text-right">Rate</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`person-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50" style={{transition:"background-color 0.15s ease"}}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F1E38]">{p.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.email} · {p.contact_phone}</div>
                </td>
                <td>
                  <div className="flex flex-col gap-1">
                    {p.person_type?.map((t) => (
                      <span key={t} className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border w-fit ${t === "engineer" ? "text-[#0F1E38] border-blue-500/40 bg-blue-500/10" : "text-violet-300 border-violet-500/40 bg-violet-500/10"}`}>{t.replace("_", " ")}</span>
                    ))}
                  </div>
                </td>
                <td className="text-slate-700">{p.region}</td>
                <td>
                  <div className="flex flex-wrap gap-1">{p.vendors?.map(v => <VendorTag key={v} vendor={v}/>)}</div>
                  <div className="text-[10px] font-mono text-violet-300 mt-0.5">{p.skills?.join(" · ")}</div>
                </td>
                <td>
                  {p.certifications?.length > 0 ? (
                    <div className="text-[10px] font-mono text-emerald-400">{p.certifications.map(c => c.code).join(" · ")}</div>
                  ) : <span className="text-slate-400 text-[10px] font-mono">—</span>}
                </td>
                <td className="text-right font-mono text-[#0F1E38] text-xs">
                  {(() => {
                    const curSym = { AUD: "A$", INR: "₹", NZD: "NZ$", PHP: "₱", USD: "$" }[p.currency] || "$";
                    return (
                      <>
                        {p.day_rate > 0 && <div>{curSym}{p.day_rate}/d</div>}
                        {p.hourly_rate > 0 && <div className="text-violet-300">{curSym}{p.hourly_rate}/hr</div>}
                      </>
                    );
                  })()}
                </td>
                <td className="pr-4">
                  <div className="flex gap-2 justify-end items-center">
                    <button data-testid={`avail-req-${p.id}`} onClick={() => requestAvailability(p.id, p.name)} title="Send availability refresh link" className="text-slate-400 hover:text-[#0F1E38]" style={{ transition: "color 0.15s ease" }}>
                      <CalendarBlank size={14} weight="bold" />
                    </button>
                    {p.timesheet_token ? (
                      <>
                        <button data-testid={`ts-copy-${p.id}`} onClick={() => copyTimesheetLink(p.timesheet_token, p.name)} title="Copy timesheet portal link" className="text-emerald-600 hover:text-emerald-700" style={{ transition: "color 0.15s ease" }}><LinkSimple size={14} weight="bold" /></button>
                        <button data-testid={`ts-revoke-${p.id}`} onClick={() => revokeTimesheetLink(p.id, p.name)} title="Revoke timesheet link" className="text-slate-400 hover:text-red-500" style={{ transition: "color 0.15s ease" }}><X size={12} /></button>
                      </>
                    ) : (
                      <button data-testid={`ts-gen-${p.id}`} onClick={() => genTimesheetLink(p.id, p.name)} title="Generate timesheet portal link" className="text-slate-400 hover:text-[#0F1E38]" style={{ transition: "color 0.15s ease" }}><Copy size={14} /></button>
                    )}
                    <button data-testid={`person-edit-${p.id}`} onClick={() => openEdit(p)} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}><PencilSimple size={14} /></button>
                    {isAdmin && <button data-testid={`person-del-${p.id}`} onClick={() => del(p.id)} className="text-slate-500 hover:text-red-400" style={{transition:"color 0.15s ease"}}><Trash size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="grid-bg py-16 text-center text-sm text-slate-500">No personnel found.</div>}
      </div>
    </div>
  );
}
